import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  MessageSquare, 
  Send, 
  User, 
  Bot, 
  Copy, 
  Trash2, 
  RefreshCw, 
  Code, 
  FileText, 
  Terminal, 
  Lightbulb,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import AdminNavigation from '@/components/AdminNavigation';
import { LoadingState } from '@/components/LoadingState';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: {
    model?: string;
    tokens?: number;
    responseTime?: number;
  };
}

interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
}

const SYSTEM_PROMPTS = {
  general: "You are an AI assistant for the SnuggleCrafters admin system. Help with system management, data analysis, troubleshooting, and administrative tasks. Provide clear, actionable responses with code examples when relevant.",
  technical: "You are a technical AI assistant for SnuggleCrafters. Focus on code review, database queries, system architecture, and debugging. Provide detailed technical solutions with proper code formatting.",
  support: "You are a customer support AI assistant for SnuggleCrafters. Help resolve user issues, analyze support tickets, and provide solutions for common problems.",
  analytics: "You are a data analytics AI assistant for SnuggleCrafters. Help analyze metrics, generate reports, identify trends, and provide data-driven insights."
};

const CodeBlock = ({ language, children }: { language?: string; children: string }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between bg-gray-800 px-4 py-2 rounded-t-lg">
        <span className="text-sm text-gray-300">{language || 'code'}</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={copyToClipboard}
          className="text-gray-300 hover:text-white"
          data-testid="button-copy-code"
        >
          {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      <SyntaxHighlighter
        language={language || 'text'}
        style={vscDarkPlus}
        customStyle={{
          margin: 0,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        }}
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
};

const MessageBubble = ({ message }: { message: ChatMessage }) => {
  const isUser = message.role === 'user';
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex max-w-[80%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser ? 'bg-blue-500 ml-3' : 'bg-gray-500 mr-3'
        }`}>
          {isUser ? <User className="h-4 w-4 text-white" /> : <Bot className="h-4 w-4 text-white" />}
        </div>
        
        <div className={`rounded-lg px-4 py-2 ${
          isUser 
            ? 'bg-blue-500 text-white' 
            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
        }`}>
          {isUser ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown
                components={{
                  code({ node, className, children, ...props }: any) {
                    const match = /language-(\w+)/.exec(className || '');
                    const language = match ? match[1] : undefined;
                    
                    if ((props as any).inline) {
                      return (
                        <code className="bg-gray-200 dark:bg-gray-600 px-1 py-0.5 rounded text-sm" {...props}>
                          {children}
                        </code>
                      );
                    }
                    
                    return (
                      <CodeBlock language={language}>
                        {String(children).replace(/\n$/, '')}
                      </CodeBlock>
                    );
                  },
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
          
          <div className="flex items-center justify-between mt-2 text-xs opacity-70">
            <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
            {message.metadata && (
              <div className="flex items-center space-x-2">
                {message.metadata.tokens && (
                  <span>{message.metadata.tokens} tokens</span>
                )}
                {message.metadata.responseTime && (
                  <span>{message.metadata.responseTime}ms</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdminAIChat() {
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [systemPrompt, setSystemPrompt] = useState<keyof typeof SYSTEM_PROMPTS>('general');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentSession?.messages]);

  // Get chat sessions
  const { data: sessions, isLoading, refetch } = useQuery({
    queryKey: ['/api/admin/ai-chat/sessions'],
    select: (response: any) => response.data as ChatSession[],
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (data: { message: string; sessionId?: string; systemPrompt: string }) =>
      apiRequest('POST', '/api/admin/ai-chat/send', data),
    onSuccess: (result: any) => {
      const updatedSession = result.session as ChatSession;
      setCurrentSession(updatedSession);
      setMessage('');
      refetch();
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error sending message",
        description: error.message || "Failed to send message to AI assistant",
        variant: "destructive",
      });
    }
  });

  // Create new session
  const createSessionMutation = useMutation({
    mutationFn: (title: string) =>
      apiRequest('POST', '/api/admin/ai-chat/sessions', { title }),
    onSuccess: (result: any) => {
      const newSession = result.session as ChatSession;
      setCurrentSession(newSession);
      refetch();
      queryClient.invalidateQueries({ 
        queryKey: ['/api/admin/ai-chat/sessions'] 
      });
    },
  });

  // Delete session
  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) =>
      apiRequest('DELETE', `/api/admin/ai-chat/sessions/${sessionId}`),
    onSuccess: () => {
      setCurrentSession(null);
      refetch();
      queryClient.invalidateQueries({ 
        queryKey: ['/api/admin/ai-chat/sessions'] 
      });
      toast({
        title: "Session deleted",
        description: "Chat session has been deleted successfully",
      });
    },
  });

  const handleSendMessage = () => {
    if (!message.trim()) return;

    const sessionId = currentSession?.id;
    sendMessageMutation.mutate({
      message: message.trim(),
      sessionId,
      systemPrompt: SYSTEM_PROMPTS[systemPrompt],
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const startNewSession = () => {
    const title = `Chat Session ${new Date().toLocaleDateString()}`;
    createSessionMutation.mutate(title);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminNavigation currentPage="AI Assistant" />
      
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-8rem)]">
          
          {/* Chat Sessions Sidebar */}
          <div className="lg:col-span-1">
            <Card className="h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Chat Sessions
                  </CardTitle>
                  <Button 
                    size="sm" 
                    onClick={startNewSession}
                    disabled={createSessionMutation.isPending}
                    data-testid="button-new-session"
                  >
                    New Chat
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-12rem)]">
                  {isLoading ? (
                    <LoadingState message="Loading sessions..." />
                  ) : sessions && sessions.length > 0 ? (
                    <div className="space-y-2 p-4">
                      {sessions.map((session) => (
                        <div
                          key={session.id}
                          className={`p-3 rounded-lg cursor-pointer transition-colors ${
                            currentSession?.id === session.id
                              ? 'bg-blue-100 dark:bg-blue-900 border-blue-300'
                              : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                          }`}
                          onClick={() => setCurrentSession(session)}
                          data-testid={`session-${session.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-sm truncate">{session.title}</h4>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteSessionMutation.mutate(session.id);
                              }}
                              className="h-6 w-6 p-0"
                              data-testid={`delete-session-${session.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {session.messages.length} messages
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {new Date(session.updatedAt).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No chat sessions yet</p>
                      <p className="text-sm">Start a new conversation</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Main Chat Interface */}
          <div className="lg:col-span-3">
            <Card className="h-full flex flex-col">
              <CardHeader className="flex-shrink-0">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="h-5 w-5" />
                    AI Assistant
                    {currentSession && (
                      <Badge variant="outline">{currentSession.title}</Badge>
                    )}
                  </CardTitle>
                  
                  <div className="flex items-center space-x-2">
                    <select
                      value={systemPrompt}
                      onChange={(e) => setSystemPrompt(e.target.value as keyof typeof SYSTEM_PROMPTS)}
                      className="px-3 py-1 text-sm border rounded-md bg-background"
                      data-testid="select-system-prompt"
                    >
                      <option value="general">General Assistant</option>
                      <option value="technical">Technical Support</option>
                      <option value="support">Customer Support</option>
                      <option value="analytics">Data Analytics</option>
                    </select>
                    
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => refetch()}
                      data-testid="button-refresh"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {systemPrompt !== 'general' && (
                  <Alert>
                    <Lightbulb className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      {systemPrompt === 'technical' && "AI is optimized for technical queries, code review, and system architecture."}
                      {systemPrompt === 'support' && "AI is optimized for customer support issues and troubleshooting."}
                      {systemPrompt === 'analytics' && "AI is optimized for data analysis, metrics, and reporting."}
                    </AlertDescription>
                  </Alert>
                )}
              </CardHeader>

              {/* Messages Area */}
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-full p-4">
                  {currentSession && currentSession.messages.length > 0 ? (
                    <div>
                      {currentSession.messages.map((msg) => (
                        <MessageBubble key={msg.id} message={msg} />
                      ))}
                      {sendMessageMutation.isPending && (
                        <div className="flex justify-start mb-4">
                          <div className="flex max-w-[80%]">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-500 mr-3 flex items-center justify-center">
                              <Bot className="h-4 w-4 text-white" />
                            </div>
                            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-4 py-2">
                              <div className="flex items-center space-x-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                <span className="text-sm text-gray-600 dark:text-gray-300">AI is thinking...</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <Bot className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                        <h3 className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2">
                          Welcome to AI Assistant
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                          Ask questions about your system, get code help, or analyze data
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Code className="h-3 w-3" />
                            Code Help
                          </Badge>
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Terminal className="h-3 w-3" />
                            System Commands
                          </Badge>
                          <Badge variant="outline" className="flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            Documentation
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>

              {/* Message Input */}
              <div className="flex-shrink-0 p-4 border-t">
                <div className="flex space-x-2">
                  <Textarea
                    ref={textareaRef}
                    value={message}
                    onChange={handleTextareaChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask your AI assistant anything..."
                    className="resize-none min-h-[40px] max-h-[120px]"
                    disabled={sendMessageMutation.isPending}
                    data-testid="textarea-message"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!message.trim() || sendMessageMutation.isPending}
                    className="self-end"
                    data-testid="button-send"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}